import { useTranslation } from 'react-i18next';
import {
  Bed,
  BedDouble,
  BedSingle,
  Sofa,
  Baby,
  Users,
  ChevronDown,
  ChevronUp,
  Check,
} from 'lucide-react';
import { useState } from 'react';
import type { SleepingConfig, RoomConfig } from '../amenities/BedConfigurator';
import { bedWidthLabel, extraBedTypeCounts } from '../amenities/BedConfigurator';
import { formatCurrency } from '@/lib/currency';

interface SleepingSectionProps {
  sleeping: SleepingConfig;
  className?: string;
  darkMode?: boolean;
  /** Extra beds that guest has requested/reserved for this booking */
  requestedExtraBeds?: Record<string, number> | null;
  /** Per-type setup notes (already translated to the guest's language). */
  extraBedTypeNotes?: Record<string, string> | null;
  /** General extra-bed note (already translated). */
  extraBedGeneralNote?: string | null;
  /** room id -> host-written room name in the guest's language. */
  translatedRoomNames?: Record<string, string> | null;
  /** Currency for the extra-bed price. */
  currency?: string;
}

// Bed type icons mapping
const BED_ICONS: Record<string, typeof Bed> = {
  single: BedSingle,
  twin: BedSingle,
  double: BedDouble,
  queen: BedDouble,
  king: BedDouble,
  super_king: BedDouble,
  sofa_bed: Sofa,
  sofa_bed_single: Sofa,
  sofa_bed_double: Sofa,
  bunk: Bed,
  bunk_bed: Bed,
  crib: Baby,
  toddler_bed: Baby,
  floor_mattress: Bed,
  air_mattress: Bed,
  extra_mattress: Bed,
  futon: Bed,
  daybed: Bed,
  murphy_bed: Bed,
};

// Room type icons
const ROOM_ICONS: Record<string, typeof Bed> = {
  bedroom: Bed,
  master_bedroom: BedDouble,
  guest_bedroom: Bed,
  kids_room: Baby,
  living_room: Sofa,
  loft: Bed,
  alcove: Bed,
  mezzanine: Bed,
  studio: Bed,
  studio_area: Bed,
};

export function SleepingSection({ sleeping, className = '', darkMode = true, requestedExtraBeds, extraBedTypeNotes, extraBedGeneralNote, translatedRoomNames, currency }: SleepingSectionProps) {
  const { t } = useTranslation();
  const [expandedRoom, setExpandedRoom] = useState<string | null>(null);

  // A room with no beds tells the guest nothing ("Bedroom 1 · 0 people") — it
  // usually means the host set the bedroom COUNT but skipped the beds step.
  const rooms = (sleeping?.rooms ?? []).filter((r) => (r.beds?.length ?? 0) > 0);
  const reserved = Object.entries(requestedExtraBeds ?? {}).filter(([, n]) => n > 0);
  // Extra beds the host offers on request — shown when this booking hasn't
  // reserved any (a config with ONLY extra beds, no rooms, used to show nothing).
  const showOffered = !!sleeping?.extraBeds?.available && reserved.length === 0;
  const offeredExtraBeds = showOffered ? Object.entries(extraBedTypeCounts(sleeping.extraBeds)) : [];
  if (!sleeping || (rooms.length === 0 && reserved.length === 0 && !showOffered)) {
    return null;
  }

  // Calculate total capacity
  const totalCapacity = sleeping.totalCapacity || calculateCapacity(rooms);

  function calculateCapacity(rooms: RoomConfig[]): number {
    const BED_SLEEPS: Record<string, number> = {
      single: 1, twin: 2, double: 2, queen: 2, king: 2, super_king: 2,
      sofa_bed: 2, sofa_bed_single: 1, sofa_bed_double: 2,
      bunk: 2, bunk_bed: 2, crib: 1, toddler_bed: 1,
      floor_mattress: 1, air_mattress: 1, extra_mattress: 1,
      futon: 1, daybed: 1, murphy_bed: 2,
    };

    return rooms.reduce((total, room) => {
      return total + room.beds.reduce((roomTotal, bed) => {
        return roomTotal + (BED_SLEEPS[bed.type] || 1) * bed.count;
      }, 0);
    }, 0);
  }

  const getRoomCapacity = (room: RoomConfig): number => {
    const BED_SLEEPS: Record<string, number> = {
      single: 1, twin: 2, double: 2, queen: 2, king: 2, super_king: 2,
      sofa_bed: 2, sofa_bed_single: 1, sofa_bed_double: 2,
      bunk: 2, bunk_bed: 2, crib: 1, toddler_bed: 1,
      floor_mattress: 1, air_mattress: 1, extra_mattress: 1,
      futon: 1, daybed: 1, murphy_bed: 2,
    };

    return room.beds.reduce((total, bed) => {
      return total + (BED_SLEEPS[bed.type] || 1) * bed.count;
    }, 0);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with summary — title is a strong text label (no icon badge);
          the group icon lives on the section header above. */}
      <div className={`flex items-center justify-between pb-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-100'}`}>
        <div>
          <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('unitDetails.sleeping.title')}
          </h3>
          {rooms.length > 0 && (
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {/* Plural forms (hr: 1 soba / 2 sobe / 5 soba); the old two-form
                  text is the fallback until guest.sleeping.roomCount exists. */}
              {t(
                'guest.sleeping.roomCount',
                `${rooms.length} ${rooms.length === 1 ? t('guest.sleeping.room', 'soba') : t('guest.sleeping.rooms', 'sobe')}`,
                { count: rooms.length },
              )}
            </p>
          )}
        </div>
        {totalCapacity > 0 && (
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
            darkMode
              ? 'bg-cyan-500/20 text-cyan-400'
              : 'bg-cyan-100 text-cyan-700'
          }`}>
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">{totalCapacity} {t('unitDetails.sleeping.people', { count: totalCapacity })}</span>
          </div>
        )}
      </div>

      {/* Rooms list */}
      <div className="space-y-2">
        {rooms.map((room, index) => {
          const RoomIcon = ROOM_ICONS[room.type] || Bed;
          const isExpanded = expandedRoom === room.id;
          const capacity = getRoomCapacity(room);

          return (
            <div
              key={room.id}
              className={`rounded-lg overflow-hidden ${
                darkMode
                  ? 'bg-slate-700/30 border border-slate-600/50'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              <button
                type="button"
                onClick={() => setExpandedRoom(isExpanded ? null : room.id)}
                className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                  darkMode
                    ? 'hover:bg-slate-700/50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <RoomIcon className={`h-4 w-4 ${darkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
                  <div className="text-left">
                    <span className={`block font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {translatedRoomNames?.[room.id] || room.name || `${t(`unitDetails.sleeping.roomTypes.${room.type}`)} ${index + 1}`}
                    </span>
                    {/* The beds themselves, visible without opening the room —
                        "where do we sleep" is the question guests actually ask. */}
                    <span className={`block text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {[
                        room.beds.map((b) => `${b.count}× ${t(`unitDetails.sleeping.bedTypes.${b.type}`)}`).join(', '),
                        `${capacity} ${t('unitDetails.sleeping.people', { count: capacity })}`,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Quick bed summary */}
                  <div className="hidden sm:flex items-center gap-1">
                    {room.beds.slice(0, 3).map((bed, i) => {
                      const BedIcon = BED_ICONS[bed.type] || Bed;
                      return (
                        <div key={i} className={`flex items-center gap-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          <BedIcon className="h-3 w-3" />
                          <span>{bed.count}</span>
                        </div>
                      );
                    })}
                  </div>
                  {isExpanded ? (
                    <ChevronUp className={`h-4 w-4 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  ) : (
                    <ChevronDown className={`h-4 w-4 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className={`px-4 py-3 border-t ${
                  darkMode
                    ? 'border-slate-600/50 bg-slate-800/30'
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex flex-wrap gap-2">
                    {room.beds.map((bed, i) => {
                      const BedIcon = BED_ICONS[bed.type] || Bed;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                            darkMode
                              ? 'bg-slate-700/50 border border-slate-600/50 text-slate-200'
                              : 'bg-white border border-gray-200 text-gray-700'
                          }`}
                        >
                          <BedIcon className={`h-4 w-4 ${darkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
                          <span>
                            {bed.count}× {t(`unitDetails.sleeping.bedTypes.${bed.type}`)}
                            {bedWidthLabel(bed.type) && (
                              <span className={`ml-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-400'}`}>
                                · {bedWidthLabel(bed.type)}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Extra beds the host offers on request (none reserved for this stay) */}
      {showOffered && (
        <div className={`p-3 rounded-lg ${
          darkMode
            ? 'bg-amber-500/10 border border-amber-500/20'
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-start gap-2">
            <Bed className={`h-4 w-4 mt-0.5 shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            <div className="text-sm min-w-0">
              <span className={`font-medium ${darkMode ? 'text-amber-200' : 'text-amber-700'}`}>
                {t('guest.sleeping.extraBedsAvailable', 'Dodatni kreveti na zahtjev')}
              </span>
              {offeredExtraBeds.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {offeredExtraBeds.map(([type, count]) => {
                    const note = extraBedTypeNotes?.[type];
                    return (
                      <li key={type} className={`text-xs ${darkMode ? 'text-amber-300/80' : 'text-amber-700'}`}>
                        <span className="font-medium">{count}× {t(`unitDetails.sleeping.bedTypes.${type}`, type)}</span>
                        {note && <span className="opacity-80"> — {note}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {extraBedGeneralNote && (
                <p className={`text-xs mt-1.5 ${darkMode ? 'text-amber-300/70' : 'text-amber-700'}`}>
                  {extraBedGeneralNote}
                </p>
              )}
              {typeof sleeping.extraBeds?.price === 'number' && sleeping.extraBeds.price > 0 && (
                <p className={`text-xs font-medium mt-1.5 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  {formatCurrency(sleeping.extraBeds.price, currency)} / {t('common.night', 'noć')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reserved extra beds - show what guest has in their booking */}
      {reserved.length > 0 && (
        <div className={`p-3 rounded-lg ${
          darkMode
            ? 'bg-emerald-500/10 border border-emerald-500/20'
            : 'bg-emerald-50 border border-emerald-200'
        }`}>
          <div className="flex items-start gap-2">
            <Check className={`h-4 w-4 mt-0.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <div className="text-sm">
              <span className={`font-medium ${darkMode ? 'text-emerald-200' : 'text-emerald-700'}`}>
                {t('guest.sleeping.reservedExtraBeds', 'Rezervirani dodatni kreveti')}
              </span>
              <div className="mt-2 space-y-2">
                {reserved.map(([type, count]) => {
                  const BedIcon = BED_ICONS[type] || Bed;
                  const note = extraBedTypeNotes?.[type];
                  return (
                    <div key={type} className="flex items-start gap-2">
                      <span
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm shrink-0 ${
                          darkMode
                            ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-200'
                            : 'bg-emerald-100 border border-emerald-200 text-emerald-700'
                        }`}
                      >
                        <BedIcon className={`h-4 w-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        {count}× {t(`unitDetails.sleeping.bedTypes.${type}`, type)}
                      </span>
                      {note && (
                        <span className={`text-xs leading-snug pt-1.5 ${darkMode ? 'text-emerald-300/80' : 'text-emerald-600'}`}>
                          {note}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {extraBedGeneralNote && (
                <p className={`text-xs mt-2 ${darkMode ? 'text-emerald-300/70' : 'text-emerald-600'}`}>
                  {extraBedGeneralNote}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SleepingSection;
